import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import Select from "react-select";
import { RootState } from "../../rootReducer";
import { ModalProps } from "../../types/common";
import { getRegionAndLanguageData } from "../../utils/regionAndLanguagesGraphQL";
import { CHANGE_USER_INFO } from "../../services/graphql/migrations";
import { setUser } from "../../pages/auth/userSlice";
import { useAppDispatch } from "../../store";
import { showAlert, showGreenAlert } from "../../util";
import {
  assignSelectedAge,
  assignSelectedAssetType,
  assignSelectedAssetTag,
  assignSelectedBrand,
  assignSelectedColorCode,
  assignSelectedColorName,
  assignSelectedFabricContent,
  assignSelectedFabricName,
  assignSelectedFileType,
  assignSelectedFilters,
  assignSelectedGender,
  assignSelectedLabelName,
  assignSelectedImageResolution,
  assignSelectedSilhouette,
  assignSelectedStyle,
  assignSelectedTotalModel,
  assignLastSelectedOption,
} from "../../pages/filterOptionsSlice";

const RegionSelectionModal = ({
  isOpen,
  toggle,
  modalClass = "region-modal",
  keyboard = true,
  backdrop = "static",
  centered = true,
}: ModalProps) => {
  const { t, i18n } = useTranslation("common");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const UserInfoSlice = useSelector((state: RootState) => state.user);
  const [tempSelectedRegion, setTempSelectedRegion] = useState<any>(null);
  const [tempSelectedLanguage, setTempSelectedLanguage] = useState<any>(null);
  const prevIsOpenRef = useRef(false);

  const {
    getUpdatedLanguageOptions,
    allRegionList,
    allLanguageList,
    defaultRegion,
    defaultLanguage,
  } = getRegionAndLanguageData([]);

  const [updateUserInfo] = useMutation(CHANGE_USER_INFO);

  // Initialize temp selections when modal opens (only when isOpen changes from false to true)
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    const isNowOpen = isOpen;

    if (isNowOpen && !wasOpen) {
      // Modal just opened - initialize temp selections
      if (
        UserInfoSlice?.primaryRegion &&
        allRegionList &&
        allRegionList.length > 0
      ) {
        const region = allRegionList.find(
          (r: any) => r.value.toString() === UserInfoSlice.primaryRegion
        );
        if (region) {
          setTempSelectedRegion(region);
        }
      } else if (defaultRegion) {
        setTempSelectedRegion(defaultRegion);
      }

      if (
        UserInfoSlice?.preferredLanguage &&
        allLanguageList &&
        allLanguageList.length > 0
      ) {
        const language = allLanguageList.find(
          (l: any) => l.value.toString() === UserInfoSlice.preferredLanguage
        );
        if (language) {
          setTempSelectedLanguage(language);
        }
      } else if (defaultLanguage) {
        setTempSelectedLanguage(defaultLanguage);
      }
    } else if (!isNowOpen && wasOpen) {
      // Modal just closed - reset temp selections when closing without saving
      setTempSelectedRegion(null);
      setTempSelectedLanguage(null);
    }

    prevIsOpenRef.current = isNowOpen;
  }, [
    isOpen,
    UserInfoSlice?.primaryRegion,
    UserInfoSlice?.preferredLanguage,
    allRegionList,
    allLanguageList,
    defaultRegion,
    defaultLanguage,
  ]);

  const handleRegionChange = (region: any) => {
    setTempSelectedRegion(region);
    setTempSelectedLanguage(null);
    // Auto-select first available language when region changes
    if (getUpdatedLanguageOptions && region) {
      const languages = getUpdatedLanguageOptions(region);
      if (languages && languages.length > 0) {
        setTempSelectedLanguage(languages[0]);
      }
    }
  };

  const handleLanguageChange = (lang: any) => {
    // If no temp region selected, use current Redux region or default
    if (!tempSelectedRegion) {
      const currentRegion = UserInfoSlice?.primaryRegion
        ? allRegionList.find(
            (r: any) => r.value.toString() === UserInfoSlice.primaryRegion
          )
        : defaultRegion;
      if (currentRegion) {
        setTempSelectedRegion(currentRegion);
      }
    }
    setTempSelectedLanguage(lang);
  };

  const emptyFilterAttributes = () => {
    dispatch(assignSelectedAssetType([]));
    dispatch(assignSelectedAssetTag([]));
    dispatch(assignSelectedBrand([]));
    dispatch(assignSelectedImageResolution([]));
    dispatch(assignSelectedFileType([]));
    dispatch(assignSelectedStyle([]));
    dispatch(assignSelectedSilhouette([]));
    dispatch(assignSelectedColorName([]));
    dispatch(assignSelectedColorCode([]));
    dispatch(assignSelectedGender([]));
    dispatch(assignSelectedAge([]));
    dispatch(assignSelectedFabricName([]));
    dispatch(assignSelectedFabricContent([]));
    dispatch(assignSelectedTotalModel([]));
    dispatch(assignSelectedLabelName([]));
    dispatch(assignSelectedFilters([]));
    dispatch(assignLastSelectedOption(""));
  };

  const updateRegionChanges = async () => {
    if (!tempSelectedRegion || !tempSelectedLanguage) return;

    // Check if region actually changed
    const currentRegionValue = UserInfoSlice?.primaryRegion?.toString();
    const newRegionValue = (tempSelectedRegion as any)?.value?.toString();
    const regionChanged = currentRegionValue !== newRegionValue;

    try {
      await updateUserInfo({
        variables: {
          id: Number(UserInfoSlice?.id),
          userInfo: {
            preferredLanguage: (tempSelectedLanguage as any)?.value,
            primaryRegion: (tempSelectedRegion as any)?.value,
          },
        },
      });

      const userInfo = {
        ...UserInfoSlice,
        preferredLanguage: (tempSelectedLanguage as any)?.value,
        primaryRegion: (tempSelectedRegion as any)?.value,
      };
      dispatch(setUser(userInfo));
      i18n.changeLanguage((tempSelectedLanguage as any)?.value);

      // Clear temp selections after successful update
      setTempSelectedRegion(null);
      setTempSelectedLanguage(null);

      // Close modal
      toggle();

      showGreenAlert(t("Updated Successfully"));
      emptyFilterAttributes();

      // Redirect to home page ONLY if region changed
      if (regionChanged) {
        navigate("/");
      }
    } catch (error) {
      console.error("Error updating user info:", error);
      showAlert(
        "error",
        "Error. Could not update language or region successfully"
      );
    }
  };

  // Get current selections: use temp if set, otherwise use Redux values
  const currentRegion = useMemo(() => {
    if (tempSelectedRegion) return tempSelectedRegion;
    if (UserInfoSlice?.primaryRegion && allRegionList) {
      return (
        allRegionList.find(
          (r: any) => r.value.toString() === UserInfoSlice.primaryRegion
        ) || defaultRegion
      );
    }
    return defaultRegion;
  }, [
    tempSelectedRegion,
    UserInfoSlice?.primaryRegion,
    allRegionList,
    defaultRegion,
  ]);

  const currentLanguage = useMemo(() => {
    if (tempSelectedLanguage) return tempSelectedLanguage;
    if (UserInfoSlice?.preferredLanguage && allLanguageList) {
      return (
        allLanguageList.find(
          (l: any) => l.value.toString() === UserInfoSlice.preferredLanguage
        ) || defaultLanguage
      );
    }
    return defaultLanguage;
  }, [
    tempSelectedLanguage,
    UserInfoSlice?.preferredLanguage,
    allLanguageList,
    defaultLanguage,
  ]);

  // Get available languages for the selected region
  const availableLanguages = useMemo(() => {
    if (getUpdatedLanguageOptions && currentRegion) {
      return getUpdatedLanguageOptions(currentRegion);
    }
    return allLanguageList;
  }, [getUpdatedLanguageOptions, currentRegion, allLanguageList]);

  return (
    <Modal
      show={isOpen}
      onHide={toggle}
      backdrop={backdrop}
      className={modalClass}
      centered={centered}
      keyboard={keyboard}
    >
      <Modal.Header closeButton>
        <h4 className="mb-0">{t("Change Region")}</h4>
      </Modal.Header>
      <Modal.Body>
        <Select
          classNamePrefix="regular-looking-dropdown"
          className="w-100"
          name="region"
          onChange={handleRegionChange}
          options={allRegionList}
          isMulti={false}
          value={currentRegion}
        />
        <Select
          classNamePrefix="regular-looking-dropdown"
          className="w-100 mt-3 mb-2"
          name="language"
          onChange={handleLanguageChange}
          options={availableLanguages}
          isMulti={false}
          value={currentLanguage}
          isDisabled={!currentRegion}
        />
        <p className="mb-0 fw-bold">
          {t("Not all styles or brands will be available in every region.")}
        </p>
      </Modal.Body>
      <Modal.Footer>
        <button
          className="btn btn-blue w-100"
          type="button"
          disabled={!(!!tempSelectedRegion && !!tempSelectedLanguage)}
          onClick={updateRegionChanges}
        >
          {t("Continue")}
        </button>
        <button
          className="btn btn-outline w-100"
          type="button"
          onClick={toggle}
        >
          {t("Cancel")}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default RegionSelectionModal;
